/**
 * Search-fold text without losing the original positions.
 *
 * Two problems at once. Arabic is the primary language here, and the same word
 * gets written several ways — with and without harakat, alef with or without its
 * hamza, final ta-marbuta where a ha was typed — so a search that compares raw
 * strings finds nothing a real person typed. Latin has the milder version of
 * this (`café` / `cafe`).
 *
 * Folding those away changes the LENGTH of the string, which is the second
 * problem: a match found at index 12 of the folded text is not at index 12 of
 * what we have to highlight. So the fold also returns a map from each folded
 * character back to the original index it came from, and highlighting is done in
 * original coordinates.
 *
 * Every pattern here is written with `\u` escapes on purpose. Harakat and
 * combining marks are invisible in an editor, and a bare range of them in source
 * is both unreadable and one re-encode away from silently changing meaning.
 */

/** Harakat and tanween, superscript alef, and tatweel — decoration that carries
 *  no distinction a searcher intends to make. */
const ARABIC_DROPPED = /[\u064B-\u065F\u0670\u0640]/u;

/** Combining diacritical marks: what NFD leaves behind once an accented Latin
 *  letter is split into letter + accent. */
const COMBINING = /[\u0300-\u036F]/u;

/** Letters written more than one way, folded to whichever form a keyboard
 *  reaches most easily. */
const ARABIC_FOLDED: Readonly<Record<string, string>> = {
  'أ': 'ا', // alef with hamza above -> alef
  'إ': 'ا', // alef with hamza below -> alef
  'آ': 'ا', // alef with madda       -> alef
  'ٱ': 'ا', // alef wasla            -> alef
  'ى': 'ي', // alef maksura          -> ya
  'ة': 'ه', // ta marbuta            -> ha
  'ؤ': 'و', // waw with hamza        -> waw
  'ئ': 'ي', // ya with hamza         -> ya
};

export interface FoldedText {
  /** The folded string, safe to compare against a folded query. */
  readonly text: string;
  /** `map[i]` is the index in the ORIGINAL string that folded character `i` came
   *  from. Length always matches `text`. */
  readonly map: readonly number[];
}

/**
 * Fold `input` for comparison, keeping a per-character trail back to it.
 *
 * Done one character at a time rather than with a chain of `.replace()` calls
 * precisely because the trail is the point: a regex pass gives the right string
 * and no way to say where any of it came from.
 */
export function fold(input: string): FoldedText {
  let text = '';
  const map: number[] = [];

  for (let index = 0; index < input.length; index += 1) {
    // `charAt` rather than `[index]`: the loop bound already proves the character
    // is there, and this is the form that says so to the type checker.
    const char = input.charAt(index);
    if (ARABIC_DROPPED.test(char)) continue;

    const substituted = ARABIC_FOLDED[char] ?? char;
    // NFD splits an accented letter into letter + accent; the accent is dropped
    // just below, so the letter contributes exactly one folded character. A
    // grapheme that expands to several contributes each of them, all pointing
    // back at this same original index.
    const decomposed = substituted.normalize('NFD').toLowerCase();

    for (const piece of decomposed) {
      if (COMBINING.test(piece)) continue;
      text += piece;
      map.push(index);
    }
  }

  return { text, map };
}

/** Fold a query and split it into terms. Empty terms are dropped, so a trailing
 *  space while typing does not make the query stop matching. */
export function foldTerms(query: string): readonly string[] {
  return fold(query.trim())
    .text.split(/\s+/u)
    .filter((term) => term.length > 0);
}
